import { DatePipe } from "@angular/common";
import { Component, computed, inject, input, OnInit, signal, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BaseChartDirective } from "ng2-charts";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { Language } from "../../../constants/enums";
import { CardEntryInfoType, DataEntry, DataEntryMetricResponse, ItemAction, TableButtonClicked } from "../../../models";
import { ChartsService, DataentryApiService, LanguageService, MetricApiService } from "../../../services";
import { ChartActionButtonsComponent } from "../../chart-action-buttons/chart-action-buttons";
import { EditDataEntryModalComponent } from "../../data-entry";
import { ConfirmationModalComponent } from "../../general";
import { MetricBreakdownModal } from "../../modals";
import { DataTableComponent } from "../../table/data-table/data-table";
import { MetricReadingEditorComponent } from "../metrics-reading-editor/metrics-reading-editor";

@Component({
  selector: "adaa-metrics-reading-details",
  imports: [
    BaseChartDirective,
    TranslatePipe,
    DataTableComponent,
    MetricReadingEditorComponent,
    ChartActionButtonsComponent,
  ],
  providers: [DatePipe],
  templateUrl: "./metrics-reading-details.html",
  styleUrl: "./metrics-reading-details.scss",
})
export class MetricsReadingDetailsComponent implements OnInit {
  private readonly _metricApiService = inject(MetricApiService);
  private readonly _toastrService = inject(ToastrService);
  private readonly _modalService = inject(NgbModal);
  private readonly _languageService = inject(LanguageService);
  private readonly _chartService = inject(ChartsService);
  private readonly _datePipe = inject(DatePipe);
  private readonly _router = inject(Router);
  private readonly _dataentryApiService = inject(DataentryApiService);
  private readonly _translateService = inject(TranslateService);

  metricId = input.required<number>();
  metricType = input.required<string | undefined>();
  isMetricPage = input.required<boolean>();
  enableActionCell = input<boolean>(false);

  readingEditor = viewChild.required<MetricReadingEditorComponent>("readingEditor");
  adaaDataTable = viewChild.required<DataTableComponent>("adaaDataTable");

  metricReadings = signal<DataEntryMetricResponse[]>([]);
  dataEntryId: number;
  activeTab: number = 1;

  public get disableEditRules(): ItemAction[] {
    const rules: ItemAction[] = [
      {
        propertyName: "wfProcessCtlId",
        operator: "notNull",
      },
    ];

    if (!AdaaHelper.isPMOEntity()) {
      rules.push({
        propertyName: "dataStatus",
        compareWith: "N",
        operator: "equal",
      });
    }

    return rules;
  }

  public get disableDeleteRules(): ItemAction[] {
    const rules: ItemAction[] = [
      {
        propertyName: "wfProcessCtlId",
        operator: "notNull",
      },
    ];

    if (!AdaaHelper.isPMOEntity()) {
      rules.push({
        propertyName: "dataStatus",
        compareWith: "N",
        operator: "equal",
      });
    }

    if (this.isDimension()) {
      rules.push({
        propertyName: "actualSynced",
        operator: "notNull",
      });
    }

    return rules;
  }

  readonly isDimension = computed(() => this.metricType() === Constants.METRICS_TYPE.DIMENSION);
  customButtons = computed(() =>
    this.metricType() === Constants.METRICS_TYPE.LOCAL
      ? [
          {
            iconName: "adaa-icon-console_agreements",
            eventName: "dataEntry",
            iconTitle: "breadcrumbs.data-entry",
          },
        ]
      : []
  );
  entryInfoMode = signal<boolean>(true);

  readonly metricTableConfig = computed(() =>
    this.isDimension()
      ? Constants.MAIN_TABLE_LIST_CONF_KEY.DATA_ENTRY_MONITORING_METRICS_LIST
      : Constants.MAIN_TABLE_LIST_CONF_KEY.DATA_ENTRY_METRICS_LIST
  );

  readonly customActionButtons = computed(() =>
    this.isDimension() && this.enableActionCell()
      ? [
          {
            eventName: "metricDetails",
            iconName: "fa-solid fa-chart-bar",
            iconTitle: "metrics.detailed_view",
          },
        ]
      : []
  );

  readonly chartLabels = computed(() =>
    this.metricReadings()
      .sort((a, b) => a.periodDate - b.periodDate)
      .map((e) => {
        return this._datePipe.transform(e.periodDate, Constants.shortMonthYearDate, Constants.uaeTimezone);
      })
  );
  readonly chartDataset = computed(() => {
    const data = this.metricReadings().sort((a, b) => a.periodDate - b.periodDate);
    const actuals = data.map((e) => e.actual);

    return [
      {
        data: actuals,
        label: this._languageService.translate("metrics.used_value"),
      },
    ];
  });
  readonly chartOptions = computed(() => {
    this._chartService.initLineChartOptions({
      isRank: true,
    });

    return {
      ...this._chartService.lineChartOptions,
      scales: {
        ...this._chartService.lineChartOptions?.scales,
        y: {
          ...this._chartService.lineChartOptions?.scales?.y,
          reverse: false,
          ticks: {
            font: {
              family: '"Open Sans", "NotoKufiArabic"',
            },
          },
        },
      },
      plugins: {
        ...this._chartService.lineChartOptions?.plugins,
        tooltip: {
          ...this._chartService.lineChartOptions?.plugins?.tooltip,
          callbacks: undefined,
        },
        legend: {
          rtl: this._languageService.current() === Language.Arabic,
          position: "bottom",
          labels: {
            usePointStyle: true,
          },
        },
        datalabels: {
          display: false,
        },
        title: {
          display: false,
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;
  });

  public ngOnInit(): void {
    this._getReadings();
  }

  public getAction(value: TableButtonClicked) {
    this.dataEntryId = value.data.id;

    switch (value.event) {
      case "edit":
        this.readingEditor().value.set(value.data.actual);
        this.readingEditor().open();
        break;
      case "delete":
        this._deleteEntry(value.data.id);
        break;
      case "view":
        this._router.navigateByUrl(`/console/data-entry/metrics/${this.metricId()}`);
        break;
      case "metricDetails":
        this._openDimensionsModal(value.data);
        break;
      case "dataEntry":
        this._getDataEntry(value.data);
        break;
      default:
        break;
    }
  }

  private _getDataEntry(event: DataEntry): void {
    this._dataentryApiService
      .getInfov2({
        itemId: this.metricId(),
        itemType: Constants.METRICS_TYPE_ID.GLOBAL_LOCAL,
        periodId: event.periodId,
      })
      .subscribe({
        next: (response) => {
          this.entryInfoMode.set(response.responseData ? true : false);
          this._openEditDataEntryModal(event, response.responseData ?? event);
        },
      });
  }

  private _openEditDataEntryModal(event: DataEntry, dataEntry: CardEntryInfoType): void {
    const modal = this._modalService.open(EditDataEntryModalComponent, {
      centered: true,
      size: "xl",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.dataEntry = dataEntry;
    modal.componentInstance.header = `${AdaaHelper.getItemValueByToken(event, "metricName")} - ${("0" + event.month).slice(-2)}/${event.year}`;

    modal.result.then((e) => {
      if (!e) return;
      const propsToDelete = [
        "recommendationsEN",
        "pmoNotesEN",
        "areasOfImprovementsEN",
        "analysisEN",
        "filename",
        "attachType",
        "entryFile",
        "status",
        "updateTS",
        "updateUser",
        "id",
      ];
      propsToDelete.forEach((prop) => delete e[prop]);

      this._dataentryApiService
        .updateCardDataEntryInfo({
          itemId: this.metricId(),
          itemType: Constants.METRICS_TYPE_ID.GLOBAL_LOCAL,
          periodId: event.periodId,
          data: {
            ...e,
            ...(this.entryInfoMode() ? { id: dataEntry.id } : {}),
            itemId: +this.metricId(),
            itemType: +Constants.METRICS_TYPE_ID.GLOBAL_LOCAL,
            periodId: event.periodId,
          },
        })
        .subscribe({
          next: (response) => {
            if (response.inError) return;
            this._toastrService.success(this._translateService.instant("notification.success.save"));
          },
        });
    });
  }

  public setValue(value: number | null) {
    let entries: DataEntryMetricResponse[] = AdaaHelper.clone(this.metricReadings());

    const entryIndex = this.metricReadings().findIndex((e) => e.id === this.dataEntryId);
    if (entryIndex === -1) return;

    const entry = entries[entryIndex];
    entry.actual = value ? value?.toString() : "";
    entries.splice(entryIndex, 1, entry);

    entries = entries.map((e) => {
      return {
        ...e,
        updated: e.id === entry.id,
        metricType: this.metricType(),
      };
    });

    this._metricApiService.updateDataEntryMetrics(entries).subscribe({
      next: (response) => {
        if (response.inError) return;
        const additionalFields = response.additionalFields as { [key: string]: string };
        if (additionalFields[Constants.CONSTANT_MEASUREMENT_UNIT_METRICS.toString()]) {
          this._toastrService.success(this._languageService.translate("data_entry.notifications.CODE_27"));
        }
      },
      complete: () => {
        this._getReadings();
      },
    });
  }

  public addReading(value: number | null | undefined, month: number, year: number) {
    if (this.metricReadings().find((entry) => entry.month === month && entry.year === year)) {
      this._toastrService.error(this._languageService.translate("data_entry.cannotAddMessage"));
      return;
    }

    const result = {
      updated: false,
      periodDate: new Date(year, month - 1).getTime(),
      month,
      year,
      actual: value?.toString(),
      metricId: this.metricId(),
      metricType: this.metricType(),
      dataStatus: "N",
      entityId: AdaaHelper.entity.id,
    };

    this._metricApiService.updateDataEntryMetrics([result]).subscribe({
      next: (response) => {
        if (response.inError) return;
        const additionalFields = response.additionalFields as { [key: string]: string };
        if (additionalFields[Constants.CONSTANT_MEASUREMENT_UNIT_METRICS.toString()]) {
          this._toastrService.warning(this._languageService.translate("data_entry.notifications.CODE_27"));
        }
        this._getReadings();
      },
    });
  }

  private _getReadings() {
    this._metricApiService.getDataEntryByMetricId(this.metricId()).subscribe({
      next: (response) => {
        if (response.inError) return;

        this.metricReadings.set(response.responseData ?? []);
      },
    });
  }

  private _deleteEntry(id: number) {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = "data_entry.metric_value_delete_yes_no_title";
    modal.componentInstance.title = "data_entry.metric_value_delete_yes_no_information";

    modal.result.then((e) => {
      if (e) {
        this._metricApiService.deleteDataEntry(id).subscribe({
          next: (response) => {
            if (response.inError) return;
            this._toastrService.success(this._languageService.translate("notification.success.remove"));
            this._getReadings();
          },
        });
      }
    });
  }

  private _openDimensionsModal(metric: DataEntryMetricResponse) {
    const modal = this._modalService.open(MetricBreakdownModal, {
      centered: true,
      size: "xl",
      modalDialogClass: this._languageService.modalDirection(),
    });

    const modalTitle = AdaaHelper.getItemValueByToken(metric, "metricName");

    modal.componentInstance.modalTitle.set(modalTitle);
    modal.componentInstance.periodId.set(metric.periodId);
    modal.componentInstance.metricId.set(this.metricId());
    modal.componentInstance.periodsDdl.set(this._getPeriodsDdl());
  }

  private _getPeriodsDdl() {
    return this.metricReadings().map((e) => {
      return {
        value: e.periodId,
        text: this._datePipe.transform(e.periodDate, Constants.shortMonthYearDate, Constants.uaeTimezone),
      };
    });
  }
}
